import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertTriangle, CheckCircle, Clock, Shield, TrendingUp, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatInCST } from '../utils/timezone';
import { Link } from 'react-router-dom';

export default function SlaAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const loadData = useCallback(async () => {
    try {
      const [alertsRes, metricsRes] = await Promise.all([
        api.get('/sla-alerts', { params: { acknowledged: filterStatus === 'active' ? 'false' : filterStatus === 'acknowledged' ? 'true' : 'all', limit: 200 } }),
        api.get('/sla-alerts/metrics')
      ]);
      let data = alertsRes.data || [];
      if (filterType !== 'all') data = data.filter(a => a.alert_type === filterType);
      setAlerts(data);
      setMetrics(metricsRes.data);
      setSelectedIds([]);
      setPage(0);
    } catch {
      toast.error('Failed to load SLA alerts');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAcknowledge = async (alertId) => {
    try {
      await api.put(`/sla-alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      loadData();
    } catch { toast.error('Failed to acknowledge'); }
  };

  const handleBulkAcknowledge = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await api.post('/sla-alerts/bulk-acknowledge', { alert_ids: selectedIds });
      toast.success(`${res.data.acknowledged_count} alert(s) acknowledged`);
      loadData();
    } catch { toast.error('Failed to bulk acknowledge'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    const pageAlerts = paginatedAlerts.filter(a => !a.acknowledged);
    if (selectedIds.length === pageAlerts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pageAlerts.map(a => a.id));
    }
  };

  const paginatedAlerts = alerts.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(alerts.length / pageSize);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="sla-alerts-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">SLA Alerts</h1>
        <p className="text-gray-600 mt-1">Monitor SLA compliance and manage breach alerts</p>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="sla-metrics">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Active Breaches</p>
                  <p className="text-2xl font-bold text-red-600">{metrics.total_active}</p>
                </div>
                <div className="p-2 rounded-lg bg-red-50">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{metrics.active_response_breaches} response, {metrics.active_resolution_breaches} resolution</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Compliance Rate</p>
                  <p className={`text-2xl font-bold ${metrics.compliance_rate >= 90 ? 'text-emerald-600' : metrics.compliance_rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{metrics.compliance_rate}%</p>
                </div>
                <div className="p-2 rounded-lg bg-emerald-50">
                  <Shield className="h-5 w-5 text-emerald-500" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{metrics.compliant_tickets} of {metrics.total_tickets_with_sla} tickets (30d)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Avg Response Breach</p>
                  <p className="text-2xl font-bold text-gray-800">{metrics.avg_breach_response_hours || '—'}h</p>
                </div>
                <div className="p-2 rounded-lg bg-amber-50">
                  <Clock className="h-5 w-5 text-amber-500" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Hours over SLA target</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total Acknowledged</p>
                  <p className="text-2xl font-bold text-gray-800">{metrics.total_acknowledged}</p>
                </div>
                <div className="p-2 rounded-lg bg-blue-50">
                  <CheckCircle className="h-5 w-5 text-blue-500" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Of {metrics.total_alerts} total alerts</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters + Bulk Actions */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" /> Breach Alerts
              <Badge variant="outline">{alerts.length}</Badge>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[160px]" data-testid="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="response_breach">Response Breach</SelectItem>
                  <SelectItem value="resolution_breach">Resolution Breach</SelectItem>
                </SelectContent>
              </Select>
              {selectedIds.length > 0 && (
                <Button onClick={handleBulkAcknowledge} size="sm" variant="outline" className="text-emerald-700 border-emerald-300" data-testid="bulk-acknowledge-btn">
                  <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                  Acknowledge ({selectedIds.length})
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {alerts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No SLA alerts found</p>
              <p className="text-sm mt-1">{filterStatus === 'active' ? 'All tickets are within SLA targets' : 'No matching alerts'}</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-2" data-testid="sla-mobile-cards">
                {paginatedAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`border rounded-lg p-3 ${alert.acknowledged ? 'opacity-60 bg-gray-50' : 'bg-white'}`}
                    data-testid={`sla-card-${alert.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/tickets/${alert.ticket_id}`} className="font-medium text-sm text-blue-600 hover:underline">
                            {alert.ticket_title || alert.ticket_id?.slice(0, 8)}
                          </Link>
                          <Badge className={alert.alert_type === 'response_breach' ? 'bg-orange-100 text-orange-800 text-xs' : 'bg-red-100 text-red-800 text-xs'}>
                            {alert.alert_type === 'response_breach' ? 'Response' : 'Resolution'}
                          </Badge>
                          {alert.acknowledged ? (
                            <Badge className="bg-gray-100 text-gray-600 text-xs">Ack'd</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 text-xs">Active</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{alert.agreement_name || '—'}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>Target: <strong>{alert.sla_target_hours}h</strong></span>
                          <span className="text-red-600">Actual: <strong>{alert.actual_hours}h</strong></span>
                          {alert.assigned_to && <span>{alert.assigned_to.split('@')[0]}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {!alert.acknowledged && (
                          <>
                            <Checkbox
                              checked={selectedIds.includes(alert.id)}
                              onCheckedChange={() => toggleSelect(alert.id)}
                            />
                            <Button size="sm" variant="ghost" onClick={() => handleAcknowledge(alert.id)}>
                              <CheckCircle className="h-4 w-4 text-emerald-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      {filterStatus !== 'acknowledged' && (
                        <Checkbox
                          checked={selectedIds.length > 0 && selectedIds.length === paginatedAlerts.filter(a => !a.acknowledged).length}
                          onCheckedChange={toggleAll}
                          data-testid="select-all-checkbox"
                        />
                      )}
                    </TableHead>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>SLA Target</TableHead>
                    <TableHead>Actual</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedAlerts.map((alert) => (
                    <TableRow key={alert.id} className={alert.acknowledged ? 'opacity-60' : ''} data-testid={`alert-row-${alert.id}`}>
                      <TableCell>
                        {!alert.acknowledged && (
                          <Checkbox
                            checked={selectedIds.includes(alert.id)}
                            onCheckedChange={() => toggleSelect(alert.id)}
                            data-testid={`alert-check-${alert.id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Link to={`/tickets/${alert.ticket_id}`} className="font-medium text-blue-600 hover:underline" data-testid={`alert-ticket-link-${alert.id}`}>
                          {alert.ticket_title || alert.ticket_id?.slice(0, 8)}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">{alert.agreement_name || '—'}</TableCell>
                      <TableCell>
                        <Badge className={alert.alert_type === 'response_breach' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}>
                          {alert.alert_type === 'response_breach' ? 'Response' : 'Resolution'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{alert.sla_target_hours}h</TableCell>
                      <TableCell className="text-sm font-semibold text-red-600">{alert.actual_hours}h</TableCell>
                      <TableCell className="text-sm">{alert.assigned_to ? alert.assigned_to.split('@')[0] : '—'}</TableCell>
                      <TableCell>
                        {alert.acknowledged ? (
                          <Badge className="bg-gray-100 text-gray-600">Acknowledged</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">{formatInCST(alert.created_at, 'MMM d, h:mm a')}</TableCell>
                      <TableCell className="text-right">
                        {!alert.acknowledged && (
                          <Button size="sm" variant="ghost" onClick={() => handleAcknowledge(alert.id)} data-testid={`ack-btn-${alert.id}`}>
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-sm text-gray-500">Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, alerts.length)} of {alerts.length}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
